import { create } from "zustand";
import {
  getFeedPosts,
  getPostById,
  getRecentPosts,
  createPost,
  updatePost,
  deletePost,
  acknowledgePost,
  getAcknowledgments,
  FeedPost,
  CreatePostData,
  UpdatePostData,
  Pagination,
  AcknowledgmentData
} from "@/services/OrganizationFeedService";
import { registerStoreReset } from "@/store/storeResetUtils";

interface OrganizationFeedStore {
  // State
  posts: FeedPost[];
  selectedPost: FeedPost | null;
  recentPosts: FeedPost[];
  acknowledgmentData: AcknowledgmentData | null;
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  currentCategory: string | null;

  // Actions
  fetchPosts: (page?: number, limit?: number, category?: string) => Promise<void>;
  fetchPostById: (id: number) => Promise<void>;
  fetchRecentPosts: (limit?: number) => Promise<void>;
  createPost: (data: CreatePostData) => Promise<boolean>;
  updatePost: (id: number, data: UpdatePostData) => Promise<boolean>;
  deletePost: (id: number) => Promise<boolean>;
  acknowledgePost: (id: number) => Promise<boolean>;
  fetchAcknowledgments: (id: number, page?: number, limit?: number) => Promise<void>;
  setSelectedPost: (post: FeedPost | null) => void;
  setCurrentCategory: (category: string | null) => void;
  resetMessages: () => void;
  resetError: () => void;
  resetStore: () => void;
}

const useOrganizationFeedStore = create<OrganizationFeedStore>((set, get) => ({
  // Initial state
  posts: [],
  selectedPost: null,
  recentPosts: [],
  acknowledgmentData: null,
  pagination: null,
  loading: false,
  error: null,
  successMessage: null,
  currentCategory: null,

  // Fetch all posts with pagination
  fetchPosts: async (page = 1, limit = 10, category?: string) => {
    try {
      set({ loading: true, error: null });

      const categoryToUse = category !== undefined ? category : get().currentCategory;
      const response = await getFeedPosts(page, limit, categoryToUse || undefined);

      if (response.success) {
        set({
          posts: response.data,
          pagination: response.pagination,
          loading: false
        });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch posts';
        set({
          error: errorMsg,
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Posts Error:", error);
      set({ error: error.message || 'An error occurred', loading: false });
    }
  },

  // Fetch a single post by ID
  fetchPostById: async (id: number) => {
    try {
      set({ loading: true, error: null });

      const response = await getPostById(id);

      if (response.success) {
        set({
          selectedPost: response.data,
          loading: false
        });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch post';
        set({
          error: errorMsg,
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Post By ID Error:", error);
      set({ error: error.message || 'An error occurred', loading: false });
    }
  },

  // Fetch recent posts for dashboard widget
  fetchRecentPosts: async (limit = 3) => {
    try {
      const response = await getRecentPosts(limit);

      if (response.success) {
        set({ recentPosts: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Recent Posts Error:", error);
    }
  },

  // Create a new post
  createPost: async (data: CreatePostData) => {
    try {
      set({ loading: true, error: null, successMessage: null });

      const response = await createPost(data);

      if (response.success) {
        // Refresh posts list
        const { pagination, currentCategory } = get();
        await get().fetchPosts(1, pagination?.pageSize || 10, currentCategory || undefined);

        set({
          loading: false,
          successMessage: 'Post created successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to create post',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Create Post Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Update an existing post
  updatePost: async (id: number, data: UpdatePostData) => {
    try {
      set({ loading: true, error: null, successMessage: null });

      const response = await updatePost(id, data);

      if (response.success) {
        // Update the post in the local state
        const posts = get().posts.map(post =>
          post.id === id ? { ...post, ...response.data } : post
        );
        set({
          posts,
          selectedPost: response.data,
          loading: false,
          successMessage: 'Post updated successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to update post',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Update Post Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Delete a post
  deletePost: async (id: number) => {
    try {
      set({ loading: true, error: null, successMessage: null });

      const response = await deletePost(id);

      if (response.success) {
        // Remove the post from local state
        const posts = get().posts.filter(post => post.id !== id);
        set({
          posts,
          loading: false,
          successMessage: 'Post deleted successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to delete post',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Delete Post Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Acknowledge a post
  acknowledgePost: async (id: number) => {
    try {
      set({ error: null });

      const response = await acknowledgePost(id);

      if (response.success) {
        // Update the post in local state
        const posts = get().posts.map(post =>
          post.id === id
            ? { ...post, user_acknowledged: true, acknowledgment_count: post.acknowledgment_count + 1 }
            : post
        );

        // Update recent posts as well
        const recentPosts = get().recentPosts.map(post =>
          post.id === id
            ? { ...post, user_acknowledged: true, acknowledgment_count: post.acknowledgment_count + 1 }
            : post
        );

        // Update selected post if it matches
        const selectedPost = get().selectedPost;
        if (selectedPost && selectedPost.id === id) {
          set({
            selectedPost: {
              ...selectedPost,
              user_acknowledged: true,
              acknowledgment_count: selectedPost.acknowledgment_count + 1
            }
          });
        }

        set({ posts, recentPosts });
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to acknowledge post' });
        return false;
      }
    } catch (error: any) {
      console.error("Acknowledge Post Error:", error);
      set({ error: error.message });
      return false;
    }
  },

  // Fetch acknowledgments for a post
  fetchAcknowledgments: async (id: number, page = 1, limit = 20) => {
    try {
      set({ loading: true, error: null });

      const response = await getAcknowledgments(id, page, limit);

      if (response.success) {
        set({
          acknowledgmentData: response.data,
          loading: false
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch acknowledgments',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Acknowledgments Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Set selected post
  setSelectedPost: (post: FeedPost | null) => set({ selectedPost: post }),

  // Set current category filter
  setCurrentCategory: (category: string | null) => set({ currentCategory: category }),

  // Reset messages
  resetMessages: () => set({ error: null, successMessage: null }),

  // Reset error
  resetError: () => set({ error: null }),

  // Reset store to initial state
  resetStore: () => set({
    posts: [],
    selectedPost: null,
    recentPosts: [],
    acknowledgmentData: null,
    pagination: null,
    loading: false,
    error: null,
    successMessage: null,
    currentCategory: null
  })
}));

// Register this store for centralized reset
registerStoreReset('organizationFeed', () => useOrganizationFeedStore.getState().resetStore());

export default useOrganizationFeedStore;
